import { RefObject, useEffect, useRef } from 'react';
import { track } from './track';

const MILESTONES = [25, 50, 75, 100] as const;

// Reports how far down an article the reader got. Registered on the article route
// only, so the landing page pays nothing for it.
export function useScrollDepth(
  slug: string | undefined,
  ref: RefObject<HTMLElement | null>
): void {
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    fired.current = new Set();
  }, [slug]);

  useEffect(() => {
    if (!slug) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const el = ref.current;
      if (!el || el.offsetHeight === 0) return;

      const read = window.scrollY + window.innerHeight - el.offsetTop;
      const pct = (read / el.offsetHeight) * 100;

      for (const milestone of MILESTONES) {
        if (pct >= milestone && !fired.current.has(milestone)) {
          fired.current.add(milestone);
          track('article-scroll', { slug, depth: milestone });
        }
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [slug, ref]);
}
